import { Casino } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from '@mui/material'
import axios from 'axios'
import { Bin } from 'chezy-inventory-backend/bin/database/types/Bin'
import { useFormik } from 'formik'
import { useCallback, useEffect } from 'react'
import * as yup from 'yup'

import { getBaseURL } from '../../utils/getBaseURL'

interface BinFormData {
  id: string
  name: string
  description?: string | undefined
  instructions?: string | undefined
}

const CreateBinForm = (props: {
  isOpen: boolean
  data?: BinFormData
  parentBin: Bin | undefined
  onDismiss: () => void
  editing?: boolean
  onSave: (data: BinFormData) => void
}) => {
  const defaultData = props.data ?? {
    id: '',
    name: '',
    description: '',
    instructions: '',
  }

  defaultData.description = defaultData.description ?? ''
  defaultData.id = defaultData.id ?? ''
  defaultData.name = defaultData.name ?? ''
  defaultData.instructions = defaultData.instructions ?? ''

  useEffect(() => {
    if (props.isOpen) {
      formik.resetForm()
      formik.validateForm()
      formik.setValues(defaultData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen])

  const validationSchema = yup.object({
    id: yup.string().required('Item id is required'),
    name: yup.string().required('Item name is required'),
    description: yup.string(),
    instructions: yup.string(),
  })

  const formik = useFormik<BinFormData>({
    initialValues: defaultData,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      props.onSave(values)
    },
  })

  const getID = useCallback(() => {
    axios.get(`${getBaseURL()}/bins/makeID`).then((resp) => {
      formik.setFieldValue('id', resp.data as string)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Dialog open={props.isOpen} onClose={props.onDismiss}>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>
          {props.editing
            ? `Editing "${props.data?.name}"`
            : `Add a bin ${props.parentBin ? `to ${props.parentBin.name}` : ''}`}
        </DialogTitle>
        <DialogContent>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              minWidth: 500,
              gap: 10,
            }}
          >
            {!props.editing && (
              <TextField
                autoFocus
                margin="dense"
                id="id"
                label="Item ID / Scan Code"
                type="text"
                fullWidth
                name="id"
                variant="standard"
                value={formik.values.id}
                onChange={formik.handleChange}
                error={formik.touched.id && Boolean(formik.errors.id)}
                helperText={formik.touched.id && formik.errors.id}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Generate random id">
                        <IconButton onClick={getID}>
                          <Casino />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            )}
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="Bin Name"
              type="text"
              fullWidth
              name="name"
              variant="standard"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
            <TextField
              autoFocus
              margin="dense"
              id="description"
              name="description"
              label="Bin Description (Optional)"
              type="text"
              fullWidth
              variant="standard"
              value={formik.values.description}
              onChange={formik.handleChange}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
            />
            <TextField
              multiline
              autoFocus
              id="instructions"
              name="instructions"
              label="Bin Packing Instructions (Optional)"
              type="text"
              fullWidth
              variant="standard"
              value={formik.values.instructions}
              onChange={formik.handleChange}
              error={formik.touched.instructions && Boolean(formik.errors.instructions)}
              helperText={formik.touched.instructions && formik.errors.instructions}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onDismiss} color="error">
            Cancel
          </Button>
          <Button type="submit">Save</Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export { CreateBinForm }
export type { BinFormData }
